import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { permissionsApi } from '@/lib/pillarApi';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { PermissionGrantEditor } from '@/components/permissions/PermissionGrantEditor';
import {
    type PermissionsGrant,
    type PermissionsBundlePublic,
    type UpdateBundleRequest,
} from '@pillar-portal/api-types';
import { AuthorizableAction, ResourceType, Status } from '@pillar-portal/database/dist/frontend';
import {
    formatPermissionEffectLabel,
    formatPermissionActionVerbList,
    formatPermissionResourceTypePluralLabel,
    PermissionGrantSummary,
} from './PermissionGrantSummary';

type PolicyPermissionBundleModalMode = 'create' | 'edit' | 'view';

interface PolicyPermissionBundleModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: PolicyPermissionBundleModalMode;
    organizationId: number | null;
    organizationUuid: string | null;
    organizationName: string;
    bundle: PermissionsBundlePublic | null;
    initialBundle?: Pick<PermissionsBundlePublic, 'name' | 'description' | 'permissions'> | null;
    copy?: Partial<PolicyPermissionBundleModalCopy>;
    allowScopeNeutralWildcard?: boolean;
    createBundle?: (
        request: PolicyPermissionBundleMutationRequest
    ) => Promise<PermissionsBundlePublic>;
    updateBundle?: (
        bundleUuid: string,
        request: UpdateBundleRequest
    ) => Promise<PermissionsBundlePublic>;
    deleteBundle?: (bundleUuid: string) => Promise<void>;
    restoreBundle?: (bundleUuid: string) => Promise<PermissionsBundlePublic>;
    onMutationSuccess?: () => void;
}

interface PolicyPermissionBundleMutationRequest {
    name: string;
    description?: string;
    permissions: PermissionsGrant[];
}

interface PolicyPermissionBundleModalCopy {
    createTitle: string;
    duplicateTitle: string;
    editTitle: string;
    viewTitle: string;
    createDescription: string;
    duplicateDescription: string;
    editDescription: string;
    viewDescription: string;
    deletedDescription: string;
    emptyPermissionsLabel: string;
    deleteConfirmationMessage: string;
    removePermissionConfirmationMessage: string;
    namePlaceholder: string;
    wildcardSourceLabel?: string;
}

const DefaultPolicyPermissionBundleModalCopy: PolicyPermissionBundleModalCopy = {
    createTitle: 'Create Policy Bundle',
    duplicateTitle: 'Duplicate Policy Bundle',
    editTitle: 'Edit Policy Bundle',
    viewTitle: 'View Policy Bundle',
    createDescription: 'Create an organization policy with typed permissions.',
    duplicateDescription: 'Create an editable copy of this organization policy.',
    editDescription: 'Edit this organization policy and its typed permissions.',
    viewDescription: 'View this organization policy and its typed permissions.',
    deletedDescription: 'Review this deleted policy bundle and restore it if needed.',
    emptyPermissionsLabel: 'No permissions in this policy bundle.',
    deleteConfirmationMessage: 'Delete this policy bundle?',
    removePermissionConfirmationMessage: 'Remove this permission from the policy bundle?',
    namePlaceholder: 'Cashier',
};

function buildEmptyPolicyPermission(): PermissionsGrant {
    return {
        effect: 'allow',
        resource_type: ResourceType.ORGANIZATION_CREDENTIAL,
        actions: [],
        resources: [],
    };
}

function clonePermissions(permissions: ReadonlyArray<PermissionsGrant>): PermissionsGrant[] {
    return permissions.map((permission) => ({
        ...permission,
        actions: [...permission.actions],
        resources: permission.resources.map((resource) => ({ ...resource })),
    }));
}

export function getDuplicatedPolicyName(name: string): string {
    const suffix = /^(.*\S) ([0-9]+)$/.exec(name);
    if (suffix === null) {
        return `${name} 1`;
    }
    const baseName = suffix[1];
    const sequence = suffix[2];
    if (baseName === undefined || sequence === undefined) {
        return `${name} 1`;
    }
    return `${baseName} ${BigInt(sequence) + 1n}`;
}

interface PolicyPermissionEditorModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    organizationUuid: string | null;
    organizationName: string;
    policyName: string;
    permission: PermissionsGrant | null;
    saveErrorMessage: string | null;
    allowScopeNeutralWildcard: boolean;
    wildcardSourceLabel?: string;
    onSavePermission: (permission: PermissionsGrant) => void;
}

function PolicyPermissionEditorModal(props: PolicyPermissionEditorModalProps) {
    const [draftPermission, setDraftPermission] = useState<PermissionsGrant>(
        buildEmptyPolicyPermission()
    );
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (!props.open) {
            setDraftPermission(buildEmptyPolicyPermission());
            setValidationError(null);
            return;
        }
        setDraftPermission(props.permission ?? buildEmptyPolicyPermission());
        setValidationError(null);
    }, [props.open, props.permission]);

    const modalDescription: string = useMemo(() => {
        if (draftPermission.actions.length === 0) {
            return 'Select actions to grant permissions for.';
        }
        const policyName =
            props.policyName.trim().length > 0 ? props.policyName.trim() : 'unnamed policy';
        return `${formatPermissionEffectLabel(draftPermission.effect)} ${formatPermissionActionVerbList(draftPermission.actions)} permissions to policy ${policyName} of ${props.organizationName} on ${formatPermissionResourceTypePluralLabel(draftPermission.resource_type)}`;
    }, [
        draftPermission.actions,
        draftPermission.effect,
        draftPermission.resource_type,
        props.organizationName,
        props.policyName,
    ]);

    const handleSave = (): void => {
        if (draftPermission.actions.length === 0) {
            setValidationError('Select at least one action');
            return;
        }
        if (draftPermission.resources.length === 0) {
            setValidationError('Select at least one source designator');
            return;
        }
        setValidationError(null);
        props.onSavePermission(draftPermission);
        props.onOpenChange(false);
    };

    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            <DialogContent
                id="policy-permission-editor-modal"
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle>
                        {props.permission === null ? 'Add Permission' : 'Edit Permission'}
                    </DialogTitle>
                    <DialogDescription>{modalDescription}</DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <PermissionGrantEditor
                        value={draftPermission}
                        onChange={(nextPermission) => {
                            setDraftPermission(nextPermission);
                            setValidationError(null);
                        }}
                        eligibleActions={Object.values(AuthorizableAction)}
                        organizationUuid={props.organizationUuid}
                        allowScopeNeutralWildcard={props.allowScopeNeutralWildcard}
                        wildcardSourceLabel={props.wildcardSourceLabel}
                    />

                    {validationError !== null && (
                        <Alert id="policy-permission-editor-validation-error" variant="destructive">
                            <AlertDescription>{validationError}</AlertDescription>
                        </Alert>
                    )}

                    {props.saveErrorMessage !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>{props.saveErrorMessage}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => props.onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button id="policy-permission-editor-save" type="button" onClick={handleSave}>
                        Save Permission
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function PolicyPermissionBundleModal(props: PolicyPermissionBundleModalProps) {
    const copy: PolicyPermissionBundleModalCopy = {
        ...DefaultPolicyPermissionBundleModalCopy,
        ...props.copy,
    };
    const bumpPolicyBundlesVersion = useDataInvalidationStore(
        (state) => state.bumpPolicyBundlesVersion
    );
    const [nameText, setNameText] = useState<string>('');
    const [descriptionText, setDescriptionText] = useState<string>('');
    const [permissions, setPermissions] = useState<ReadonlyArray<PermissionsGrant>>([]);

    const [isPermissionEditorOpen, setIsPermissionEditorOpen] = useState<boolean>(false);
    const [editingPermissionIndex, setEditingPermissionIndex] = useState<number | null>(null);
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false);
    const [pendingRemovePermissionIndex, setPendingRemovePermissionIndex] = useState<number | null>(
        null
    );

    const [isConfirmRemovePermissionOpen, setIsConfirmRemovePermissionOpen] =
        useState<boolean>(false);
    const bundleDeserializationError: string | null =
        props.bundle?.permissions_deserialization_error ?? null;
    const hasInvalidPersistedPermissions: boolean = bundleDeserializationError !== null;
    const isDeletedBundle: boolean = props.bundle?.status === Status.DELETED;
    const isReadOnly: boolean = props.mode === 'view' || isDeletedBundle;

    useEffect(() => {
        if (!props.open) {
            return;
        }
        if (props.mode === 'create') {
            const initialBundle = props.initialBundle ?? null;
            setNameText(initialBundle === null ? '' : getDuplicatedPolicyName(initialBundle.name));
            setDescriptionText(initialBundle?.description ?? '');
            setPermissions(
                initialBundle === null ? [] : clonePermissions(initialBundle.permissions)
            );
            setEditingPermissionIndex(null);
            return;
        }
        if (props.bundle === null) {
            return;
        }
        setNameText(props.bundle.name);
        setDescriptionText(props.bundle.description ?? '');
        setPermissions(props.bundle.permissions);
        setEditingPermissionIndex(null);
    }, [props.open, props.mode, props.bundle, props.initialBundle]);

    const normalizedDescription: string | undefined = useMemo(() => {
        const trimmed: string = descriptionText.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }, [descriptionText]);

    const editingPermission: PermissionsGrant | null = useMemo(() => {
        if (editingPermissionIndex === null) {
            return null;
        }
        return permissions[editingPermissionIndex] ?? null;
    }, [editingPermissionIndex, permissions]);
    const designatorLabelByKey = useMemo(() => {
        const nextLabelByKey: Record<string, string> = {};
        for (const metadata of props.bundle?.resource_display_metadata ?? []) {
            nextLabelByKey[metadata.designator_key] = metadata.label;
        }
        return nextLabelByKey;
    }, [props.bundle?.resource_display_metadata]);

    const createMutation = useMutation({
        mutationFn: async () => {
            const createName: string = nameText.trim();
            if (createName.length === 0) {
                throw new Error('Name is required');
            }
            const request: PolicyPermissionBundleMutationRequest = {
                name: createName,
                description: normalizedDescription,
                permissions: [...permissions],
            };
            if (props.createBundle !== undefined) {
                return await props.createBundle(request);
            }
            if (props.organizationUuid === null) {
                throw new Error('Organization is required to create policy bundles');
            }
            return await permissionsApi.createPolicyPermissionBundleForOrganization(
                props.organizationUuid,
                request
            );
        },
        onSuccess: () => {
            props.onMutationSuccess?.();
            if (props.onMutationSuccess === undefined) {
                bumpPolicyBundlesVersion();
            }
            props.onOpenChange(false);
        },
        onError: (err) => {
            console.error('Error creating policy bundle:', err);
        },
    });

    const updateMutation = useMutation({
        mutationFn: async () => {
            if (props.bundle === null) {
                throw new Error('Missing bundle to update');
            }
            const updateName: string = nameText.trim();
            if (updateName.length === 0) {
                throw new Error('Name is required');
            }
            const request: UpdateBundleRequest = {
                name: updateName,
                description: normalizedDescription,
                permissions: [...permissions],
                expected_permissions: props.bundle.permissions,
            };
            if (props.updateBundle !== undefined) {
                return await props.updateBundle(props.bundle.permissions_bundle_uuid, request);
            }
            return await permissionsApi.updatePolicyPermissionBundle(
                props.bundle.permissions_bundle_uuid,
                request
            );
        },
        onSuccess: () => {
            props.onMutationSuccess?.();
            if (props.onMutationSuccess === undefined) {
                bumpPolicyBundlesVersion();
            }
            props.onOpenChange(false);
        },
        onError: (err) => {
            console.error('Error updating policy bundle:', err);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (props.bundle === null) {
                throw new Error('Missing bundle to delete');
            }
            if (props.deleteBundle !== undefined) {
                return await props.deleteBundle(props.bundle.permissions_bundle_uuid);
            }
            return await permissionsApi.deletePolicyPermissionBundle(
                props.bundle.permissions_bundle_uuid
            );
        },
        onSuccess: () => {
            props.onMutationSuccess?.();
            if (props.onMutationSuccess === undefined) {
                bumpPolicyBundlesVersion();
            }
            props.onOpenChange(false);
        },
        onError: (err) => {
            console.error('Error deleting policy bundle:', err);
        },
    });

    const undeleteMutation = useMutation({
        mutationFn: async () => {
            if (props.bundle === null) {
                throw new Error('Missing bundle to restore');
            }
            if (props.restoreBundle !== undefined) {
                return await props.restoreBundle(props.bundle.permissions_bundle_uuid);
            }
            return await permissionsApi.updatePolicyPermissionBundle(
                props.bundle.permissions_bundle_uuid,
                { status: Status.ACTIVE }
            );
        },
        onSuccess: () => {
            props.onMutationSuccess?.();
            if (props.onMutationSuccess === undefined) {
                bumpPolicyBundlesVersion();
            }
            props.onOpenChange(false);
        },
        onError: (err) => {
            console.error('Error restoring policy bundle:', err);
        },
    });

    const errorMessage: string | null = useMemo(() => {
        const createError: unknown = createMutation.error;
        if (createError instanceof Error) {
            return createError.message;
        }
        const updateError: unknown = updateMutation.error;
        if (updateError instanceof Error) {
            return updateError.message;
        }
        const deleteError: unknown = deleteMutation.error;
        if (deleteError instanceof Error) {
            return deleteError.message;
        }
        const undeleteError: unknown = undeleteMutation.error;
        if (undeleteError instanceof Error) {
            return undeleteError.message;
        }
        return null;
    }, [createMutation.error, deleteMutation.error, undeleteMutation.error, updateMutation.error]);

    const isSaving: boolean = createMutation.isPending || updateMutation.isPending;
    const isDeleting: boolean = deleteMutation.isPending || undeleteMutation.isPending;

    return (
        <>
            <Dialog open={props.open} onOpenChange={props.onOpenChange}>
                <DialogContent
                    id="policy-permission-bundle-modal"
                    className="max-w-3xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle>
                            {props.mode === 'create'
                                ? props.initialBundle === null || props.initialBundle === undefined
                                    ? copy.createTitle
                                    : copy.duplicateTitle
                                : props.mode === 'edit'
                                  ? copy.editTitle
                                  : copy.viewTitle}
                        </DialogTitle>
                        <DialogDescription>
                            {props.mode === 'create'
                                ? props.initialBundle === null || props.initialBundle === undefined
                                    ? copy.createDescription
                                    : copy.duplicateDescription
                                : isDeletedBundle
                                  ? copy.deletedDescription
                                  : props.mode === 'edit'
                                    ? copy.editDescription
                                    : copy.viewDescription}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                        {errorMessage !== null && (
                            <Alert id="policy-permission-bundle-error" variant="destructive">
                                <AlertDescription>{errorMessage}</AlertDescription>
                            </Alert>
                        )}

                        {hasInvalidPersistedPermissions && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {bundleDeserializationError}
                                    {
                                        ' Saving this bundle will overwrite the invalid persisted permissions.'
                                    }
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                                id="policy-permission-bundle-name"
                                value={nameText}
                                onChange={(e) => setNameText(e.target.value)}
                                placeholder={copy.namePlaceholder}
                                disabled={isReadOnly}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                id="policy-permission-bundle-description"
                                value={descriptionText}
                                onChange={(e) => setDescriptionText(e.target.value)}
                                placeholder="Optional"
                                disabled={isReadOnly}
                            />
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label>Permissions</Label>
                                {!isReadOnly && (
                                    <Button
                                        id="policy-permission-bundle-add-permission"
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setEditingPermissionIndex(null);
                                            setIsPermissionEditorOpen(true);
                                        }}
                                    >
                                        + Permission
                                    </Button>
                                )}
                            </div>

                            {permissions.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    {copy.emptyPermissionsLabel}
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {permissions.map((permission, index) => (
                                        <div
                                            key={index}
                                            data-policy-permission-summary={index}
                                            className="border rounded-lg p-4"
                                        >
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1">
                                                    <PermissionGrantSummary
                                                        permission={permission}
                                                        designatorLabelByKey={designatorLabelByKey}
                                                    />
                                                </div>

                                                {!isReadOnly && (
                                                    <div className="flex gap-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                setEditingPermissionIndex(index);
                                                                setIsPermissionEditorOpen(true);
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            onClick={() => {
                                                                setPendingRemovePermissionIndex(
                                                                    index
                                                                );
                                                                setIsConfirmRemovePermissionOpen(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        {props.mode === 'edit' && !isDeletedBundle && (
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={() => setIsConfirmDeleteOpen(true)}
                                disabled={isSaving || isDeleting}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => props.onOpenChange(false)}
                            disabled={isSaving || isDeleting}
                        >
                            {isReadOnly ? 'Close' : 'Cancel'}
                        </Button>
                        {isDeletedBundle && props.bundle !== null ? (
                            <Button
                                type="button"
                                onClick={() => {
                                    undeleteMutation.mutate();
                                }}
                                disabled={isSaving || isDeleting}
                            >
                                {undeleteMutation.isPending ? 'Restoring...' : 'Undelete'}
                            </Button>
                        ) : null}
                        {!isReadOnly && (
                            <Button
                                id="policy-permission-bundle-save"
                                type="button"
                                onClick={() => {
                                    if (props.mode === 'create') {
                                        createMutation.mutate();
                                        return;
                                    }
                                    updateMutation.mutate();
                                }}
                                disabled={isSaving || isDeleting}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <PolicyPermissionEditorModal
                open={isPermissionEditorOpen}
                onOpenChange={(nextOpen) => {
                    setIsPermissionEditorOpen(nextOpen);
                    if (!nextOpen) {
                        setEditingPermissionIndex(null);
                    }
                }}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                policyName={nameText}
                permission={editingPermission}
                saveErrorMessage={errorMessage}
                allowScopeNeutralWildcard={props.allowScopeNeutralWildcard === true}
                wildcardSourceLabel={copy.wildcardSourceLabel}
                onSavePermission={(permission) => {
                    if (editingPermissionIndex === null) {
                        setPermissions([...permissions, permission]);
                        return;
                    }
                    setPermissions(
                        permissions.map((currentPermission, index) => {
                            return index === editingPermissionIndex
                                ? permission
                                : currentPermission;
                        })
                    );
                    setEditingPermissionIndex(null);
                }}
            />

            <ConfirmationModal
                open={isConfirmDeleteOpen}
                onOpenChange={setIsConfirmDeleteOpen}
                message={copy.deleteConfirmationMessage}
                confirmLabel="Delete"
                onConfirm={() => {
                    setIsConfirmDeleteOpen(false);
                    deleteMutation.mutate();
                }}
                onCancel={() => setIsConfirmDeleteOpen(false)}
                isLoading={isDeleting}
            />

            <ConfirmationModal
                open={isConfirmRemovePermissionOpen}
                onOpenChange={setIsConfirmRemovePermissionOpen}
                message={copy.removePermissionConfirmationMessage}
                confirmLabel="Remove"
                onConfirm={() => {
                    if (pendingRemovePermissionIndex === null) {
                        return;
                    }
                    setPermissions(
                        permissions.filter((_permission, index) => {
                            return index !== pendingRemovePermissionIndex;
                        })
                    );
                    setPendingRemovePermissionIndex(null);
                    setIsConfirmRemovePermissionOpen(false);
                }}
                onCancel={() => {
                    setPendingRemovePermissionIndex(null);
                    setIsConfirmRemovePermissionOpen(false);
                }}
            />
        </>
    );
}
